import { useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { getAllDistricts, getAllRegions } from "@/Services/regions.service";
import { getMemberPhotoUrl } from "@/Services/institutions.service";

type DistrictRow = { id: string; name: string | null };
type RegionRow   = { id: string; name: string | null; district_id: string; district_name: string | null };

export interface MemberFormValues {
  first_name: string;
  last_name: string;
  second_last_name: string;
  dni: string;
  birth_date: string;
  gender: "M" | "F" | "";
  email: string;
  cell_phone: string;
  district_id: string;
  region_id: string;
  photoFile: File | null;
}

export function defaultMemberFormValues(): MemberFormValues {
  return {
    first_name: "",
    last_name: "",
    second_last_name: "",
    dni: "",
    birth_date: "",
    gender: "",
    email: "",
    cell_phone: "",
    district_id: "",
    region_id: "",
    photoFile: null,
  };
}

interface Props {
  values: MemberFormValues;
  onChange: (v: MemberFormValues) => void;
  disabled?: boolean;
  existingPhotoFilename?: string | null;
}

export function MemberForm({ values, onChange, disabled, existingPhotoFilename }: Props) {
  const [districts, setDistricts] = useState<DistrictRow[]>([]);
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [photoPreview, setPhotoPreview] = useState<string | null>(
    getMemberPhotoUrl(existingPhotoFilename ?? null),
  );

  useEffect(() => {
    getAllDistricts().then((d) =>
      setDistricts(
        d
          .map((x) => ({ id: x.id, name: x.name ?? null }))
          .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
      ),
    );
    getAllRegions().then((r) => {
      const mapped = r.map((x) => ({
        id: x.id,
        name: x.name ?? null,
        district_id: x.district_id,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        district_name: (x as any).district?.name ?? null,
      }));
      mapped.sort((a, b) => {
        const d = (a.district_name ?? "").localeCompare(b.district_name ?? "");
        return d !== 0 ? d : (a.name ?? "").localeCompare(b.name ?? "");
      });
      setRegions(mapped);
    });
  }, []);

  const filteredRegions = values.district_id
    ? regions.filter((r) => r.district_id === values.district_id)
    : regions;

  function set<K extends keyof MemberFormValues>(key: K, val: MemberFormValues[K]) {
    onChange({ ...values, [key]: val });
  }

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    set("photoFile", file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPhotoPreview(url);
    }
  }

  const inputCls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

  return (
    <div className="space-y-4">
      {/* Photo */}
      <div className="flex items-center gap-4">
        <div className="relative size-20 shrink-0 overflow-hidden rounded-full bg-muted">
          {photoPreview ? (
            <img src={photoPreview} alt="Foto" className="size-full object-cover" />
          ) : (
            <div className="flex size-full items-center justify-center">
              <Camera className="size-8 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Foto de perfil</label>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            disabled={disabled}
            className="block text-xs text-muted-foreground file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-xs file:font-medium file:text-primary-foreground"
          />
        </div>
      </div>

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Nombre *</label>
          <input type="text" value={values.first_name} onChange={(e) => set("first_name", e.target.value)} disabled={disabled} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Apellido paterno *</label>
          <input type="text" value={values.last_name} onChange={(e) => set("last_name", e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium">Apellido materno</label>
        <input type="text" value={values.second_last_name} onChange={(e) => set("second_last_name", e.target.value)} disabled={disabled} className={inputCls} />
      </div>

      {/* DNI + birth date */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">CI / DNI</label>
          <input type="text" value={values.dni} onChange={(e) => set("dni", e.target.value)} disabled={disabled} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Fecha de nacimiento</label>
          <input type="date" value={values.birth_date} onChange={(e) => set("birth_date", e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      </div>

      {/* Gender */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Género</label>
        <div className="flex gap-2">
          {([["M", "Masculino"], ["F", "Femenino"]] as const).map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => set("gender", values.gender === val ? "" : val)}
              disabled={disabled}
              className={`flex-1 rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${
                values.gender === val
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:bg-accent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Correo</label>
          <input type="email" value={values.email} onChange={(e) => set("email", e.target.value)} disabled={disabled} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Teléfono</label>
          <input type="tel" value={values.cell_phone} onChange={(e) => set("cell_phone", e.target.value)} disabled={disabled} className={inputCls} />
        </div>
      </div>

      {/* District + Region */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Distrito</label>
          <select
            value={values.district_id}
            onChange={(e) => { set("district_id", e.target.value); set("region_id", ""); }}
            disabled={disabled}
            className={inputCls}
          >
            <option value="">Sin distrito</option>
            {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Región</label>
          <select
            value={values.region_id}
            onChange={(e) => set("region_id", e.target.value)}
            disabled={disabled}
            className={inputCls}
          >
            <option value="">Sin región</option>
            {filteredRegions.map((r) => (
              <option key={r.id} value={r.id}>
                {!values.district_id && r.district_name ? `${r.district_name} — ` : ""}{r.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
